/**
 * Manage All Routes from one file
 */

import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { DashboardOutlined, ConfirmationNumberOutlined } from "@mui/icons-material";
import { BarChart3, HelpCircle, Users, Settings, Users2, User, Wrench, MessageSquare, FileText, Star } from "lucide-react";
import { ROLES, EMP_ROLES } from "./config";
// Auth pages — static imports (small, needed immediately for unauthenticated routes)
import LoginUser from "../app/auth/LoginUser";
import RegisterUser from "../app/auth/RegisterUser";
import ForgotPassword from "../app/auth/ForgotPassword";
import ResetPassword from "../app/auth/ResetPassword";
import CompleteRegistration from "../app/auth/CompleteRegistration";
import VerifyEmail from "../app/auth/VerifyEmail";
import VerifyNewEmail from "../app/auth/VerifyNewEmail";
import VerifyEmailSent from "../components/auth/VerificationSent";
import FirebaseActionHandler from "../app/auth/FirebaseActionHandler";
import RemoveCompanyUserAccount from "../app/auth/RemoveCompanyUserAccount";
import TermsOfUse, { PrivacyPolicy } from "../app/auth/TermsAndCondition";

/** Matches `app-route.js`: Employee login is resolved to `employeerole` (e.g. "Admin") for access checks. */
const ACCOUNT_SETTINGS_ROLES = [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN];

// App pages — lazy-loaded (JS downloaded only when user first navigates to the route)
const HomeContainer = lazy(() => import("../app/home/home.container"));
const ProfileContainer = lazy(() => import("../app/Profile/Profile"));
const Employee = lazy(() => import("../app/Employee/Employee"));
const ViewEmployeeApp = lazy(() => import("../app/Employee/ViewEmployeeApp"));
const CreateClient = lazy(() => import("../app/Clients/CreateClient"));
const Clients = lazy(() => import("../app/Clients/Clients"));
const CreateChannel = lazy(() => import("../app/Messages/CreateChannel"));
const ManageChannel = lazy(() => import("../app/Messages/ManageChannel"));
const LiveChat = lazy(() => import("../app/Messages/LiveChat"));
const CreateEmployee = lazy(() => import("../app/Employee/CreateEmployee"));
const ManageGroups = lazy(() => import("../app/Employee/ManageGroups"));
const InactiveEmployee = lazy(() => import("../app/Employee/InActiveEmployee"));
const Supplies = lazy(() => import("../app/Supplies/Cleaning/Supplies"));
const SuppliesManage = lazy(() => import("../app/Supplies/Cleaning/SuppliesManage"));
const ManageSuppliesRequestDetails = lazy(() => import("../app/Supplies/Cleaning/ManageSupplyRequestDetails/ManageSupplyRequestDetails"));
const SuppliesArchiveRequestDetails = lazy(() => import("../app/Supplies/Cleaning/SuppliesArchivesDetails"));
const ArchiveSupplies = lazy(() => import("../app/Supplies/Cleaning/SuppliesArchives"));
const IssueManagement = lazy(() => import("../app/IssueManagement/IssueManagement"));
const IssueDetails = lazy(() => import("../app/IssueManagement/IssueDetails/IssueDetails"));
const IssueArchive = lazy(() => import("../app/IssueManagement/IssueArchive"));
const IssueArchiveDetails = lazy(() => import("../app/IssueManagement/IssueDetails/IssueArchiveDetails"));
const Notification = lazy(() => import("../app/Notification/Notification"));
const CreateNotification = lazy(() => import("../app/Notification/CreateNotification"));
const AppoinmentSchedule = lazy(() => import("../app/Scheduler/AppoinmentSchedule"));
const ManageAppointments = lazy(() => import("../app/Scheduler/ManageAppointments"));
const ProofOfService = lazy(() => import("../app/Scheduler/ProofOfService"));
const ProofofWork = lazy(() => import("../app/Scheduler/ProofofWork"));
const AccountSettings = lazy(() => import("../app/Accounts/Settings"));
const AppointmentPageSetting = lazy(() => import("../app/Accounts/PageSettings/AppointmentSetting"));
const NotificationPageSetting = lazy(() => import("../app/Accounts/PageSettings/NotificationSetting"));
const AlertsPageSetting = lazy(() => import("../app/Accounts/PageSettings/AlertsSetting"));
const ReportsPageSetting = lazy(() => import("../app/Accounts/PageSettings/ReportsSetting"));
const ChatPageSetting = lazy(() => import("../app/Accounts/PageSettings/ChatSetting"));
const SuppliePageSetting = lazy(() => import("../app/Accounts/PageSettings/SupplieSetting"));
const IssuePageSetting = lazy(() => import("../app/Accounts/PageSettings/IssueSetting"));
const CompanyPageSetting = lazy(() => import("../app/Accounts/PageSettings/CompanySetting"));
const InvoiceSetting = lazy(() => import("../app/Accounts/PageSettings/InvoiceSetting"));
const ProofOfServiceSetting = lazy(() => import("../app/Accounts/PageSettings/ProofOfServiceSetting"));
const PrivacyDataSettings = lazy(() => import("../app/Accounts/PageSettings/PrivacyDataSettings"));
/** Old URL; bookmarks still hit /payments */
const PaymentsRedirectToInvoiceSettings = () => (
  <Navigate to="/account/settings/invoiceSetting" replace />
);
const UpgradePage = lazy(() => import("../app/Accounts/Upgrade/Upgrade"));
const Support = lazy(() => import("../app/Accounts/Support"));
const ContactUs = lazy(() => import("../app/Accounts/ContactUs"));
const ManageSupport = lazy(() => import("../app/Accounts/ManageSupport"));
const ManageSupportDetails = lazy(() => import("../app/Accounts/ManageSupportDetails"));
const ArchiveSupport = lazy(() => import("../app/Accounts/SupportArchive"));
const ArchiveSupportDetails = lazy(() => import("../app/Accounts/SupportArchiveDetails"));
const ManageChannelDetails = lazy(() => import("../app/Messages/ManageChannelDetails"));
const ClientSummaryReport = lazy(() => import("../app/Clients/ClientSummaryReport"));
const InvoiceCreate = lazy(() => import("../app/Invoices/InvoiceCreate"));
const InvoiceManage = lazy(() => import("../app/Invoices/InvoiceManage"));
const InvoicePaySuccess = lazy(() => import("../app/Invoices/InvoicePaySuccess"));
const InvoicePayPage = lazy(() => import("../app/Invoices/InvoicePayPage"));
const InvoicingListPage = lazy(() => import("../app/invoicing/InvoicingListPage"));
const InvoiceBuilderPage = lazy(() => import("../app/invoicing/InvoiceBuilderPage"));
const InvoicingDetailPage = lazy(() => import("../app/invoicing/InvoicingDetailPage"));
const ServiceTypesInvoicingPage = lazy(() => import("../app/invoicing/ServiceTypesInvoicingPage"));
const PaymentHistoryPage = lazy(() => import("../app/invoicing/PaymentHistoryPage"));
const ServiceTypesSetting = lazy(() => import("../app/Accounts/PageSettings/ServiceTypesSetting"));
const ClientBillingPage = lazy(() => import("../app/Clients/ClientBillingPage"));
const AllAlertPage = lazy(() => import("../app/Alerts/AllAlertPage"));
const EmployeeAlertPage = lazy(() => import("../app/Alerts/Employee/EmoloyeeAlertPage"));
const ClientAlertPage = lazy(() => import("../app/Alerts/Client/ClientAlertPage"));
const SuppliesAlertPage = lazy(() => import("../app/Alerts/Supplies/SuppliesAlertPage"));
const IssuesAlertPage = lazy(() => import("../app/Alerts/Issues/IssuesAlertPage"));
const SystemAlertPage = lazy(() => import("../app/Alerts/Systems/SystemAlertPage"));
const ClientReport = lazy(() => import("../app/CrewReport/ClientReports/ClientReport"));
const ClientReportOld = lazy(() => import("../app/CrewReport/ClientReports/OldClientReports"));
const DeliveredReports = lazy(() => import("../app/CrewReport/ClientReports/DeliveredReports"));
const Shiftreport = lazy(() => import("../app/CrewReport/ClientReports/OldReportList/ShiftReport"));
const WorkSiteReport = lazy(() => import("../app/CrewReport/ClientReports/OldReportList/WorkSiteReport"));
const TaskReport = lazy(() => import("../app/CrewReport/ClientReports/OldReportList/TaskReport"));
const IssuesReports = lazy(() => import("../app/CrewReport/ClientReports/OldReportList/IssuesReport"));
const SupplyReport = lazy(() => import("../app/CrewReport/ClientReports/OldReportList/SupplyReport"));
const UpcomingAppointments = lazy(() => import("../app/CrewReport/ClientReports/ReportList/UpcomingAppointments"));
const RescheduleAndCancelAppointment = lazy(() => import("../app/CrewReport/ClientReports/ReportList/ReschduleAndCancel"));
const RepeatVisitsNoShows = lazy(() => import("../app/CrewReport/ClientReports/ReportList/RepeatVisitsNoShows"));
const MostActiveClient = lazy(() => import("../app/CrewReport/ClientReports/ReportList/MostActiveClient"));
const TaskCompletionSummary = lazy(() => import("../app/CrewReport/ClientReports/ReportList/TaskCompletionSummary"));
const OverdueTasks = lazy(() => import("../app/CrewReport/ClientReports/ReportList/OverdueTasks"));
const ClientIssueTrendsReport = lazy(() => import("../app/CrewReport/ClientReports/ReportList/ClientIssueReport"));
const AvgResolutionTime = lazy(() => import("../app/CrewReport/ClientReports/ReportList/AvgResolutionTime"));
const SupplyUsageReport = lazy(() => import("../app/CrewReport/ClientReports/ReportList/SupplyUsageReport"));
const UrgentRequestsSummary = lazy(() => import("../app/CrewReport/ClientReports/ReportList/UrgentRequestsSummary"));
const ClientNotificationLog = lazy(() => import("../app/CrewReport/ClientReports/ReportList/ClientNotificationLog.jsx"));
const DeliveryIssuesReport = lazy(() => import("../app/CrewReport/ClientReports/ReportList/DeliveryIssuesReport.jsx"));
const ProofOfServiceReport = lazy(() => import("../app/CrewReport/ClientReports/ReportList/ProofOfServiceReport"));
const EmployeeReport = lazy(() => import("../app/CrewReport/EmployeeReports/EmployeeReport"));
const EmployeeReportOld = lazy(() => import("../app/CrewReport/EmployeeReports/OldEmployeeReports"));
const CheckInCheckOut = lazy(() => import("../app/CrewReport/EmployeeReports/OldReportList/CheckInCheckOut"));
const ActivityReport = lazy(() => import("../app/CrewReport/EmployeeReports/OldReportList/ActivityReport"));
const TimeSheet = lazy(() => import("../app/CrewReport/EmployeeReports/OldReportList/TimeSheet"));
const CheckInCheckOutNew = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/CheckInCheckOut"));
const ActivityReportNew = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/ActivityReport"));
const ShiftVariance = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/ShiftVarianceReport"));
const MissedShift = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/MissedShift"));
const IssueResolutionLog = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/IssueResolutionLog"));
const TasksPerEmployee = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/TaskPerEmployee"));
const EscalationTracker = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/IssueEscalation"));
const EmployeeSupplyRequests = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/EmployeeSupplyRequests"));
const UrgentSupplyPatterns = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/UrgentSupplyPatterns"));
const MessagesSenttoEmployee = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/MessagesSenttoEmployee"));
const UnReadMessage = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/UnReadMessage"));
const EmployeePerformanceSummary = lazy(() => import("../app/CrewReport/EmployeeReports/ReportList/EmployeePerformanceSummary"));
const EmailReportsOld = lazy(() => import("../app/CrewReport/OldEmailReports"));
const CreateAdmin = lazy(() => import("../app/SuperUser/Admin/CreateAdmin"));
const ManageAdmin = lazy(() => import("../app/SuperUser/Admin/ManageUserAdmin"));
const CreateCompany = lazy(() => import("../app/SuperUser/Company/CreateCompany"));
const ManageCompany = lazy(() => import("../app/SuperUser/Company/ManageCompany"));
const ManageCompanyDetails = lazy(() => import("../app/SuperUser/Company/Details/ManageCompanyDetails"));
const CompanySubscription = lazy(() => import("../app/SuperUser/Company/CompanySubscription"));
const ManageSuperUser = lazy(() => import("../app/SuperUser/ManageSuperUser"));
const CreateSuperUser = lazy(() => import("../app/SuperUser/CreateSuperUser"));
const SuperUserDashboard = lazy(() => import("../app/SuperUser/Dashboard/superUserDashboard"));
const SuperUserStripeConnect = lazy(() => import("../app/SuperUser/StripeConnect/SuperUserStripeConnect"));
const Analytics = lazy(() => import("../app/SuperUser/Analytics/Analytics"));
const SuperUserSettings = lazy(() => import("../app/SuperUser/Settings/Settings"));
const Accounts = lazy(() => import("../app/SuperUser/Accounts/Accounts"));
const AccountDetails = lazy(() => import("../app/SuperUser/Accounts/Details/AccountDetails"));
const ManageUsers = lazy(() => import("../app/SuperUser/Users/Users"));
const UserDetails = lazy(() => import("../app/SuperUser/Users/Details/UserDetails"));
const HelpTours = lazy(() => import("../app/Help/Tour/HelpTour"));
// Testing: reviews + review settings routes disabled
// const ReviewsRedirect = lazy(() => import("../app/Reviews/ReviewsRedirect"));
// const ReviewsOverview = lazy(() => import("../app/Reviews/ReviewsOverview"));
// const ReviewsInternal = lazy(() => import("../app/Reviews/ReviewsInternal"));
// const ReviewsInternalDetail = lazy(() => import("../app/Reviews/ReviewsInternalDetail"));
// const ReviewsGoogle = lazy(() => import("../app/Reviews/ReviewsGoogle"));
// const ReviewsPageSetting = lazy(() => import("../app/Accounts/PageSettings/ReviewsSetting"));
const ReviewForm = lazy(() => import("../app/Reviews/ReviewForm"));
const GoogleReviewRedirect = lazy(() => import("../app/Reviews/GoogleReviewRedirect"));

/**
 * Array of menu items will show on left sidebar
 */
const sidebarMenuList = [
  // {
  //   label: "Create Client",
  //   to: "/peoples/createClient",
  //   classes: "button",
  //   icon: <AddBoxIcon />,
  //   roles: [ROLES.ADMIN],
  // },
  {
    label: "Home",
    to: "/dashboard",
    icon: <DashboardOutlined />,
    pkgAccess: "DASHBOARD",
    roles: [ROLES.SUPPORT, EMP_ROLES.SUPPORT , ROLES.COMPANYUSER, ROLES.CLIENT, EMP_ROLES.ADMIN],
  },
  // {
  //   label: "Profile",
  //   to: "/account/profile",
  //   pkgAccess: "PROFILE",
  //   icon: <PersonOutlinedIcon />,
  //   roles: [ROLES.SUPPORT.toLowerCase(), ROLES.EMPLOYEE.toLowerCase(), EMP_ROLES.CLIENT_TEAM.toLowerCase(), EMP_ROLES.GUEST.toLowerCase()],
  // },
  {
    label: "People",
    to: "/peoples",
    icon: <Users2 size={20}/>,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
    childItems: [
      { to: "/peoples/manageEmp", label: "Employees"},
      { to: "/peoples/manageClient", label: "Clients"},
    ],
  },
  // {
  //   label: "Invoices",
  //   to: "/invoicing",
  //   icon: <FileText size={20} />,
  //   roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  //   childItems: [
  //     { to: "/invoicing", label: "Manage Invoices", pkgAccess: "MANAGE_INVOICE" },
  //     { to: "/invoicing/create", label: "Create Invoice", pkgAccess: "CREATE_INVOICE" },
  //     { to: "/invoicing/payment-history", label: "Payment History", pkgAccess: "MANAGE_INVOICE" },
  //     { to: "/invoicing/service-types", label: "Service types", pkgAccess: "CREATE_INVOICE" },
  //   ],
  // },
  {
    label: "Operations",
    to: "/operations",
    icon: <Wrench size={20}/>,
    roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
    childItems: [
      { to: "/operations/manageAppointments", label: "Schedule"},
      { to: "/operations/proofOfService", label: "Proof of Service"},
      { to: "/operations/issues/manage", label: "Issues"},
      {
      label: "Supplies",
      to: "/supplies",
      roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
      childItems: [
        { to: "/supplies/cleaning/review", label: "New Request" },
        { to: "/supplies/cleaning/manage", label: "Manage" }
      ]
    }
    ],
  },
  {
    label: "Operations",
    to: "/operations",
    icon: <Wrench size={20}/>,
    roles: [ROLES.CLIENT],
    childItems: [
      { to: "/operations/manageAppointments", label: "Schedule"},
      { to: "/operations/proofOfService", label: "Proof of Service"},
      { to: "/operations/issues/manage", label: "Issues"},
    ],
  },
  {
    label: "Supplies",
    to: "/supplies",
    icon: <Wrench size={20}/>,
    roles: [ROLES.SUPPORT.toLowerCase(), EMP_ROLES.SUPPORT],
    childItems: [
      { to: "/supplies/cleaning/review", label: "New Request"},
      { to: "/supplies/cleaning/manage", label: "Manage"},
    ],
  },
  {
    label: "Communications",
    to: "/communications",
    icon: <MessageSquare size={20} />,
    roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
    childItems: [
      { to: "/communications/message-board/live-chat", label: "Live Chat"},
      { to: "/communications/message-board/manage-channel", label: "Channels"},
      { to: "/communications/notifications/manage", label: "Notifications"},
    ],
  },
  {
    label: "Communications",
    to: "/communications",
    icon: <MessageSquare size={20} />,
    roles: [ROLES.CLIENT, ROLES.ADMIN],
    childItems: [
      { to: "/communications/message-board/live-chat", label: "Live Chat"},
    ],
  },
  {
    label: "Communications",
    to: "/communications",
    icon: <MessageSquare size={20} />,
    roles: [ROLES.SUPPORT.toLowerCase(), EMP_ROLES.SUPPORT],
    childItems: [
      { to: "/communications/notifications/manage", label: "Notifications"},
    ],
  },
  // {
  //   label: "Reviews",
  //   to: "/reviews",
  //   icon: <Star size={20} />,
  //   roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  //   childItems: [
  //     { to: "/reviews/overview", label: "Overview" },
  //     { to: "/reviews/internal", label: "Internal Reviews" },
  //     { to: "/reviews/google", label: "Google Reviews" },
  //   ],
  // },
  {
    label: "Reports",
    to: "/reports",
    icon: <FileText size={20} />,
    roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
    childItems: [
      {
        label: "Standard Reports",
        to: "/reports",
        pkgAccess: "STANDARD_REPORTS",
        roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
        childItems: [
          { to: "/reports/Clientreport", label: "Client Reports"},
          { to: "/reports/Employeereport", label: "Team Reports"},
        ]
      },
      {
        label: "Advanced Reports",
        to: "/reports",
        pkgAccess: "ADVANCED_REPORTS",
        roles: [ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
        childItems: [
          { to: "/reports/newreport/clientreport", label: "Client Reports"},
          { to: "/reports/newreport/employeereport", label: "Team Reports"},
        ]
      }
    ],
  },
  {
    label: "Reports",
    to: "/reports",
    icon: <FileText size={20} />,
    roles: [ROLES.CLIENT],
    childItems: [
      { to: "/reports/delivered-reports", label: "Delivered reports" },
    ],
  },
  {
    icon: <User size={20} />,
    label: "Account",
    to: "/account",
    roles: [ROLES.COMPANYUSER],
    childItems: [
      { to: "/account/companySubscription", label: "Billing"},
      { to: "/account/userAdmin", label: "Administration"},
      { to: "/account/upgrade", label: "Plans"},
      { to: "/account/settings", label: "Settings"},
    ],
  },
  {
    icon: <User size={20} />,
    label: "Account",
    to: "/account",
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN],
    childItems: [
      { to: "/account/userAdmin", label: "Administration"},
      { to: "/account/settings", label: "Settings"},
    ],
  },
  {
    icon: <User size={20} />,
    label: "Account",
    to: "/account",
    roles: [ROLES.SUPPORT.toLowerCase(), EMP_ROLES.SUPPORT, ROLES.EMPLOYEE.toLowerCase(), EMP_ROLES.CLIENT_TEAM.toLowerCase(), EMP_ROLES.GUEST.toLowerCase()],
    childItems: [
    { to: "/account/profile", label: "Profile", pkgAccess: "PROFILE"},
    ],
  },
  {
    icon: <HelpCircle size={20} />,
    label: "Help",
    to: "/help",
    roles: [ROLES.EMPLOYEE, ROLES.SUPPORT.toLowerCase(), EMP_ROLES.SUPPORT, EMP_ROLES.CLIENT_TEAM.toLowerCase(), EMP_ROLES.GUEST.toLowerCase()],
    childItems: [
      { to: "/help/helpTour", label: "Tour"},
      { to: "https://help.proteams.io", label: "Docs"},
    ],
  },
  {
    icon: <HelpCircle size={20} />,
    label: "Help",
    to: "/help",
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.EMPLOYEE, EMP_ROLES.ADMIN],
    childItems: [
      { to: "/help/helpTour", label: "Tour"},
      { to: "/help/manageSupport", label: "Support"},
      { to: "https://help.proteams.io", label: "Docs"},
    ],
  },
  {
    icon: <HelpCircle size={20} />,
    label: "Help",
    to: "/help",
    roles: [ROLES.CLIENT],
    childItems: [
      { to: "/help/manageSupport", label: "Support"},
    ],
  },
  // {
  //   label: "Alerts",
  //   to: "/alerts",
  //   pkgAccess: "ALERTS",
  //   icon: <ReportProblemOutlined />,
  //   roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //   childItems: [
  //     {
  //       to: "/allAlerts",
  //       pkgAccess: "ALL_ALERTS",
  //       label: "All Alerts",
  //       roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //     },
  //     {
  //       to: "/clientAlerts",
  //       label: "Client Alerts",
  //       pkgAccess: "CLIENT_ALERTS",
  //       roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //     },
  //     {
  //       to: "/empAlerts",
  //       pkgAccess: "EMPLOYEE_ALERTS",
  //       label: "Employee Alerts",
  //       roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //     },
  //     {
  //       to: "/suppliesAlert",
  //       pkgAccess: "SUPPLIES_ALERTS",
  //       label: "Supplies Alerts",
  //       roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //     },
  //     {
  //       to: "/issuesAlert",
  //       pkgAccess: "ISSUES_ALERTS",
  //       label: "Issues Alerts",
  //       roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //     },
  //     {
  //       to: "/systemAlert",
  //       pkgAccess: "SYSTEM_ALERTS",
  //       label: "System Alerts",
  //       roles: [ROLES.SUPPORT.toLowerCase(), ROLES.ADMIN.toLowerCase(), ROLES.COMPANYUSER],
  //     },
  //   ],
  // },
  {
    label: "Dashboard",
    to: "/superUserDashboard",
    pkgAccess: "Dashboard",
    icon: <BarChart3 />,
    roles: [ROLES.SUPERUSER],
  },
  {
    label: "Support Tickets",
    to: "/help",
    pkgAccess: "SUPPORT",
    icon: <ConfirmationNumberOutlined />,
    childItems: [
      { to: "/help/manageSupport", label: "Manage", pkgAccess: "MANAGE_SUPPORT", roles: [ROLES.SUPERUSER] },
      { to: "/help/archiveSupport", label: "Archive", pkgAccess: "ARCHIVE_SUPPORT", roles: [ROLES.SUPERUSER]  },
    ],
    roles: [ROLES.SUPERUSER],
  },
    {
    label: "Accounts",
    to: "/accounts",
    pkgAccess: "Accounts",
    icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>, 
    roles: [ROLES.SUPERUSER],
  },
  {
    label: "Users",
    to: "/manageUsers",
    pkgAccess: "Users",
    icon: <Users className=""/>,
    roles: [ROLES.SUPERUSER],
  },
  {
    label: "Analytics",
    to: "/analytics",
    pkgAccess: "Analytics",
    icon: <BarChart3 />,
    roles: [ROLES.SUPERUSER],
  },
  {
    label: "Settings",
    to: "/superUserSettings",
    pkgAccess: "SuperUserSettings",
    icon: <Settings />,
    roles: [ROLES.SUPERUSER],
  },
  // {
  //   label: "Account ",
  //   to: "/myAccounts",
  //   pkgAccess: "ACCOUNTS",
  //   icon: <ManageAccountsOutlined />,
  //   roles: [ROLES.ADMIN],
  //   childItems: [
  //     { to: "/userAdmin", label: "Administration", pkgAccess: "MANAGE_ADMIN"},
  //     { to: "/account/settings", label: "Settings", pkgAccess: "SETTINGS"},
  //   ],
  // },
  {
    label: 'User Management',
    to: "/superUser",
    icon: <User size={20} />,
    childItems: [
      { to: "/superUser", label: "Super User" },
      { to: "/company", label: "Company" },
      { to: "/account/userAdmin", label: "Company Admin" },
      { to: "/peoples/manageClient", label: "Company Client" },
      { to: "/peoples/manageEmp", label: "Company Employee" },
      { to: "/account/companySubscription", label: "Company Subscription" },
      { to: "/superUser/stripe-connect", label: "Stripe Connect" },
    ],
    roles: [ROLES.SUPERUSER],
  },
  // { to: "/dashboard", label: "Dashboard" },
  // {
  //   to: "",
  //   label: "Settings",
  //   heading: true,
  //   childItems: [
  //     { to: "/sidechild1", label: "SideChild1" },

  //   ],
  // },
 
];

/**
 * Routs array for authentication pages
 */
const authRoute = [
  {
    path: "/",
    component: LoginUser,
    exact: true,
    index: 1.1,
    secure: false,
    pkgAccess: "LOGIN",
    layout: "blank",
  },
  {
    path: "/login",
    component: LoginUser,
    exact: true,
    index: 1,
    pkgAccess: "LOGIN",
    layout: "blank",
    secure: false,
  },
  {
    path: "/register",
    component: RegisterUser,
    exact: true,
    index: 2,
    pkgAccess: "REGISTER",
    layout: "blank",
    secure: false,
  },
  {
    path: "/forgotpassword",
    component: ForgotPassword,
    exact: true,
    pkgAccess: "FORGOT_PASSWORD",
    index: 3,
    layout: "blank",
    secure: false,
  },
  {
    path: "/updatepassword",
    component: ResetPassword,
    exact: true,
    pkgAccess: "UPDATE_PASSWORD",
    index: 3,
    layout: "blank",
    secure: false,
  },
  {
    path: "/complete-registration",
    component: CompleteRegistration,
    exact: true,
    pkgAccess: "COMPLETE_REGISTRATION",
    index: 3,
    layout: "blank",
    secure: false,
  },
  {
    path: "/verify-email",
    component: VerifyEmail,
    exact: true,
    pkgAccess: "VERIFY",
    index: 4,
    secure: false,
    layout: "blank"
  },
  {
    path: "/verify-new-email",
    component: VerifyNewEmail,
    exact: true,
    pkgAccess: "VERIFY",
    index: 4,
    secure: false,
    layout: "blank"
  },
  {
    path: "/verify-email-sent",
    component: VerifyEmailSent,
    exact: true,
    pkgAccess: "VERIFY_EMAIL",
    index: 5,
    secure: false,
    layout: "blank"
  },
  {
    path: "/firebase-action-handler",
    component: FirebaseActionHandler,
    exact: true,
    pkgAccess: "FIREBASE_ACTION",
    index: 6,
    secure: false,
    layout: "blank"
  },
  {
    path: "/removeCompanyUserAccount",
    component: RemoveCompanyUserAccount,
    exact: true,
    pkgAccess: "REMOVE_ACCOUNT",
    index: 6,
    secure: false,
    layout: "blank"
  },
  {
    path: "/firebase-action-handler",
    component: FirebaseActionHandler,
    exact: true,
    pkgAccess: "FIREBASE_ACTION",
    index: 6,
    secure: false,
    layout: "blank"
  },
  {
    path: "/terms-of-use",
    component: TermsOfUse,
    exact: true,
    pkgAccess: "TERM_OF_USE",
    index: 6,
    secure: false,
    layout: "blank"
  },
  {
    path: "/privacy-policy",
    component: PrivacyPolicy,
    exact: true,
    pkgAccess: "PRIVACY_POLICY",
    index: 6,
    secure: false,
    layout: "blank"
  },
  {
    path: "/review/:reviewRequestId",
    component: ReviewForm,
    exact: true,
    index: 6.5,
    secure: false,
    layout: "none",
  },
  {
    path: "/google-review-redirect/:googleReviewRequestId",
    component: GoogleReviewRedirect,
    exact: true,
    index: 7,
    secure: false,
    layout: "none",
  },
];

/***
 * routes array for secure ( if user is loggedin)
 */
const secureRoutes = [
  {
    path: "/dashboard",
    component: HomeContainer,
    exact: true,
    index: 0,
    pkgAccess: "DASHBOARD",
    secure: true,
    roles: [ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, ROLES.CLIENT, EMP_ROLES.ADMIN, EMP_ROLES.SUPPORT],
  },
  {
    path: "/ViewEmployeeApp/:employeeId",
    component: ViewEmployeeApp,
    exact: true,
    index: 0,
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.ADMIN, EMP_ROLES.SUPPORT],
  },
  {
    path: "/",
    component: HomeContainer,
    exact: true,
    index: 0,
    pkgAccess: "DASHBOARD",
    secure: true,
    roles: [ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, ROLES.CLIENT, EMP_ROLES.ADMIN, EMP_ROLES.SUPPORT],
  },
  {
    path: "/newDashboard",
    component: ManageAppointments,
    exact: true,
    index: 0,
    pkgAccess: "DASHBOARD",
    secure: true,
    roles: [ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, ROLES.CLIENT, EMP_ROLES.ADMIN, EMP_ROLES.SUPPORT],
  },
  {
    path: "/account/profile",
    component: ProfileContainer,
    exact: true,
    index: 10,
    pkgAccess: "PROFILE",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.SUPPORT.toLowerCase(), EMP_ROLES.CLIENT_TEAM.toLowerCase(), EMP_ROLES.GUEST.toLowerCase(), ROLES.COMPANYUSER, ROLES.CLIENT, EMP_ROLES.ADMIN, EMP_ROLES.SUPPORT],
  },
  {
    path: "/peoples/manageEmp",
    component: Employee,
    exact: true,
    index: 11,
    pkgAccess: "MANAGE_EMPLOYEE",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/createEmp/:employeeId",
    component: CreateEmployee,
    exact: true,
    index: 12,
    pkgAccess: "CREATE_EMPLOYEE",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/createEmp",
    component: CreateEmployee,
    exact: true,
    index: 12,
    pkgAccess: "CREATE_EMPLOYEE",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/manageGroups",
    component: ManageGroups,
    exact: true,
    index: 13,
    pkgAccess: "MANAGE_GROUPS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/inactiveEmp",
    component: InactiveEmployee,
    exact: true,
    index: 13,
    pkgAccess: "INACTIVE_EMPLOYEE",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/manageClient",
    component: Clients,
    exact: true,
    index: 11,
    pkgAccess: "MANAGE_CLIENT",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/createClient/:clientId",
    component: CreateClient,
    exact: true,
    index: 12,
    pkgAccess: "CREATE_CLIENT",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/createClient",
    component: CreateClient,
    exact: true,
    index: 11,
    pkgAccess: "CREATE_CLIENT",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/peoples/client/:clientId/billing",
    component: ClientBillingPage,
    exact: true,
    index: 11.5,
    pkgAccess: "MANAGE_CLIENT",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPERUSER, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/supplies/cleaning/review",
    component: Supplies,
    exact: true,
    pkgAccess: "SUPPLIES",
    index: 13.1,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/supplies/cleaning/manage/:requestId",
    component: ManageSuppliesRequestDetails,
    exact: true,
    index: 13.21,
    pkgAccess: "MANAGE_SUPPLIES",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/supplies/cleaning/manage",
    component: SuppliesManage,
    exact: true,
    pkgAccess: "MANAGE_SUPPLIES",
    index: 13.2,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/supplies/cleaning/archives",
    component: ArchiveSupplies,
    exact: true,
    index: 13.3,
    pkgAccess: "ARCHIVE_SUPPLIES",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/supplies/cleaning/archives/:requestId",
    component: SuppliesArchiveRequestDetails,
    exact: true,
    index: 13.4,
    pkgAccess: "ARCHIVE_SUPPLIES",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/supplies/cleaning",
    component: Supplies,
    exact: true,
    index: 13,
    pkgAccess: "SUPPLIES",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/operations/issues/manage",
    component: IssueManagement,
    exact: true,
    index: 16,
    pkgAccess: "MANAGE_ISSUES",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/issues/manage/:issueId",
    component: IssueDetails,
    exact: true,
    pkgAccess: "MANAGE_ISSUES",
    index: 16.1,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/issues/archive",
    component: IssueArchive,
    exact: true,
    pkgAccess: "ARCHIVE_ISSUES",
    index: 16,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/issues/archive/:issueId",
    component: IssueArchiveDetails,
    exact: true,
    pkgAccess: "ARCHIVE_ISSUES",
    index: 16,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/communications/notifications/manage",
    component: Notification,
    exact: true,
    index: 17,
    pkgAccess: "MANAGE_NOTIFICATION",
    secure: true,
    roles: [ EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/communications/notifications/create",
    component: CreateNotification,
    exact: true,
    index: 17,
    pkgAccess: "CREATE_NOTIFICATION",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/allAlerts",
    component: AllAlertPage,
    exact: true,
    index: 18,
    pkgAccess: "ALL_ALERTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/empAlerts",
    component: EmployeeAlertPage,
    exact: true,
    index: 18,
    pkgAccess: "EMPLOYEE_ALERTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  }, {
    path: "/clientAlerts",
    component: ClientAlertPage,
    exact: true,
    index: 18,
    pkgAccess: "CLIENT_ALERTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  }, {
    path: "/suppliesAlert",
    component: SuppliesAlertPage,
    exact: true,
    index: 18,
    pkgAccess: "SUPPLIES_ALERTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  }, {
    path: "/issuesAlert",
    component: IssuesAlertPage,
    exact: true,
    pkgAccess: "ISSUES_ALERTS",
    index: 18,
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  }, {
    path: "/systemAlert",
    component: SystemAlertPage,
    exact: true,
    index: 18,
    pkgAccess: "SYSTEM_ALERTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.SUPPORT.toLowerCase(), ROLES.COMPANYUSER, EMP_ROLES.SUPPORT],
  },
  {
    path: "/admin/clientSummary",
    component: ClientSummaryReport,
    exact: true,
    pkgAccess: "CLIENT_SUMMARY",
    index: 19,
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/reports/delivered-reports",
    component: DeliveredReports,
    exact: true,
    index: 20,
    secure: true,
    roles: [ROLES.CLIENT],
  },
  {
    path: "/reports/newreport/clientreport",
    component: ClientReport,
    exact: true,
    index: 20,
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/reports/newreport/upcomingAppointments",
    component: UpcomingAppointments,
    exact: true,
    index: 20,
    pkgAccess: "ADV_APPOINTMENT_SCHEDULE_HEALTH",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/cancelAndReschduleAppointment",
    component: RescheduleAndCancelAppointment,
    exact: true,
    index: 20,
    pkgAccess: "ADV_APPOINTMENT_SCHEDULE_HEALTH",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/repeatVisitsNoShows",
    component: RepeatVisitsNoShows,
    exact: true,
    index: 20,
    pkgAccess: "ADV_APPOINTMENT_SCHEDULE_HEALTH",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/mostActiveClient",
    component: MostActiveClient,
    exact: true,
    index: 20,
    pkgAccess: "ADV_APPOINTMENT_SCHEDULE_HEALTH",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/taskCompletionSummary",
    component: TaskCompletionSummary,
    exact: true,
    index: 20,
    pkgAccess: "ADV_CLIENT_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/overdueTasks",
    component: OverdueTasks,
    exact: true,
    index: 20,
    pkgAccess: "ADV_CLIENT_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/supply-usage",
    component: SupplyUsageReport,
    exact: true,
    index: 20,
    pkgAccess: "ADVANCED_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/urgent-requests",
    component: UrgentRequestsSummary,
    exact: true,
    index: 20,
    pkgAccess: "ADVANCED_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/client-notification-log",
    component: ClientNotificationLog,
    exact: true,
    index: 20,
    pkgAccess: "ADVANCED_REPORTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/reports/newreport/delivery-issues",
    component: DeliveryIssuesReport,
    exact: true,
    index: 20,
    pkgAccess: "ADVANCED_REPORTS",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/reports/newreport/proofOfService",
    component: ProofOfServiceReport,
    exact: true,
    index: 20,
    pkgAccess: "PROOF_OF_SERVICE_REPORT",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  },
  {
    path: "/reports/ShiftReport",
    component: Shiftreport,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Worksitereport",
    component: WorkSiteReport,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Taskreport",
    component: TaskReport,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Issuesreport",
    component: IssuesReports,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Supplyreport",
    component: SupplyReport,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/clientIssueTrends",
    component: ClientIssueTrendsReport,
    exact: true,
    index: 20,
    pkgAccess: "ADV_ISSUE_RESOLUTION_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/avgResolutionTime",
    component: AvgResolutionTime,
    exact: true,
    index: 20,
    pkgAccess: "ADV_ISSUE_RESOLUTION_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Employeereport",
    component: EmployeeReportOld,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Clientreport",
    component: ClientReportOld,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/employeereport",
    component: EmployeeReport,
    exact: true,
    index: 20,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Checkincheckout",
    component: CheckInCheckOut,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Activityreports",
    component: ActivityReport,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/Timesheet",
    component: TimeSheet,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/checkInCheckOut",
    component: CheckInCheckOutNew,
    exact: true,
    index: 20,
    pkgAccess: "ADV_TIME_ATTENDANCE_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/unReadMessage",
    component: UnReadMessage,
    exact: true,
    index: 20,
    pkgAccess: "ADV_TIME_ATTENDANCE_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/shiftVariance",
    component: ShiftVariance,
    exact: true,
    index: 20,
    pkgAccess: "ADVANCED_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/missedShift",
    component: MissedShift,
    exact: true,
    index: 20,
    pkgAccess: "ADVANCED_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/issueResolutionLog",
    component: IssueResolutionLog,
    exact: true,
    index: 20,
    pkgAccess: "ADV_ISSUE_RESOLUTION_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/escalationTracker",
    component: EscalationTracker,
    exact: true,
    index: 20,
    pkgAccess: "ADV_ISSUE_RESOLUTION_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/empSupplyRequest",
    component: EmployeeSupplyRequests,
    exact: true,
    index: 20,
    pkgAccess: "ADV_TEAM_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/urgentSupplyPatterns",
    component: UrgentSupplyPatterns,
    exact: true,
    index: 20,
    pkgAccess: "ADV_TEAM_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/messagesEndToEmployee",
    component: MessagesSenttoEmployee,
    exact: true,
    index: 20,
    pkgAccess: "ADV_TEAM_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/tasksPerEmployee",
    component: TasksPerEmployee,
    exact: true,
    index: 20,
    pkgAccess: "ADV_EMPLOYEE_SUMMARY_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/activityReports",
    component: ActivityReportNew,
    exact: true,
    index: 20,
    pkgAccess: "ADV_TIME_ATTENDANCE_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/reports/newreport/employeeOverAllPerformance",
    component: EmployeePerformanceSummary,
    exact: true,
    index: 20,
    pkgAccess: "ADV_EMPLOYEE_SUMMARY_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/emailreports",
    component: EmailReportsOld,
    exact: true,
    index: 20,
    pkgAccess: "STANDARD_REPORTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/communications/message-board/create-channel",
    component: CreateChannel,
    exact: true,
    index: 20,
    pkgAccess: "CREATE_CHANNEL",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/communications/message-board/create-channel/:channelId",
    component: CreateChannel,
    exact: true,
    pkgAccess: "CREATE_CHANNEL",
    index: 20,
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/communications/message-board/manage-channel",
    component: ManageChannel,
    exact: true,
    index: 20,
    pkgAccess: "MANAGE_CHANNEL",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/communications/message-board/manage-channel/:channelId",
    component: ManageChannelDetails,
    exact: true,
    index: 20,
    pkgAccess: "MANAGE_CHANNEL",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/communications/message-board/live-chat",
    component: LiveChat,
    exact: true,
    index: 20,
    pkgAccess: "LIVE_CHAT",
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/appoinmentSchedule",
    component: AppoinmentSchedule,
    exact: true,
    index: 21,
    pkgAccess: "CREATE_APPOINTMENT",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/appoinmentSchedule/:appointmentId",
    component: AppoinmentSchedule,
    exact: true,
    index: 21,
    pkgAccess: "CREATE_APPOINTMENT",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/manageAppointments",
    component: ManageAppointments,
    exact: true,
    index: 21,
    pkgAccess: "MANAGE_APPOINTMENT",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/operations/proofOfService",
    component: ProofOfService,
    exact: true,
    index: 21,
    pkgAccess: "APPOINTMENTS",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  // {
  //   path: "/invoicing/payment-history",
  //   component: PaymentHistoryPage,
  //   exact: true,
  //   index: 21,
  //   pkgAccess: "MANAGE_INVOICE",
  //   secure: true,
  //   roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  // },
  // {
  //   path: "/invoicing/service-types",
  //   component: ServiceTypesInvoicingPage,
  //   exact: true,
  //   index: 21,
  //   pkgAccess: "CREATE_INVOICE",
  //   secure: true,
  //   roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  // },
  // {
  //   path: "/invoicing/create",
  //   component: InvoiceBuilderPage,
  //   exact: true,
  //   index: 21,
  //   pkgAccess: "CREATE_INVOICE",
  //   secure: true,
  //   roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  // },
  // {
  //   path: "/invoicing/:invoiceId/edit",
  //   component: InvoiceBuilderPage,
  //   exact: true,
  //   index: 21,
  //   pkgAccess: "CREATE_INVOICE",
  //   secure: true,
  //   roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  // },
  // {
  //   path: "/invoicing/:invoiceId",
  //   component: InvoicingDetailPage,
  //   exact: true,
  //   index: 21,
  //   pkgAccess: "MANAGE_INVOICE",
  //   secure: true,
  //   roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  // },
  // {
  //   path: "/invoicing",
  //   component: InvoicingListPage,
  //   exact: true,
  //   index: 21,
  //   pkgAccess: "MANAGE_INVOICE",
  //   secure: true,
  //   roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  // },
  {
    path: "/invoices/create",
    component: InvoiceCreate,
    exact: true,
    index: 21,
    pkgAccess: "CREATE_INVOICE",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/invoices/manage",
    component: InvoiceManage,
    exact: true,
    index: 21,
    pkgAccess: "MANAGE_INVOICE",
    secure: true,
    roles: [EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/invoices/pay",
    component: InvoicePayPage,
    exact: true,
    index: 0,
    layout: "blank",
    secure: false,
  },
  {
    path: "/invoices/pay/success",
    component: InvoicePaySuccess,
    exact: true,
    index: 0,
    layout: "blank",
    secure: false,
  },
  {
    path: "/account/settings",
    component: AccountSettings,
    exact: true,
    index: 22,
    pkgAccess: "SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/upgrade",
    component: UpgradePage,
    exact: true,
    index: 22,
    pkgAccess: "UPGRADE",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/help/support",
    component: Support,
    exact: true,
    index: 22,
    pkgAccess: "MANAGE_SUPPORT",
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.CLIENT],
  },
  {
    path: "/contactUs",
    component: ContactUs,
    exact: true,
    index: 22,
    pkgAccess: "CONTACT_US",
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/manageSupportDetails/:supportID",
    component: ManageSupportDetails,
    exact: true,
    index: 22,
    pkgAccess: "SUPPORT_DETAILS",
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.SUPERUSER, ROLES.CLIENT],
  },
  {
    path: "/help/manageSupportDetails/:supportID",
    component: ManageSupportDetails,
    exact: true,
    index: 22,
    pkgAccess: "SUPPORT_DETAILS",
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.SUPERUSER, ROLES.CLIENT],
  },
  {
    path: "/help/manageSupport",
    component: ManageSupport,
    exact: true,
    index: 22,
    pkgAccess: "MANAGE_SUPPORT",
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.COMPANYUSER, ROLES.SUPERUSER, ROLES.CLIENT],
  },
  {
    path: "/help/archiveSupportDetails/:supportID",
    component: ArchiveSupportDetails,
    exact: true,
    index: 22,
    pkgAccess: "ARCHIVE_SUPPORT_DETAILS",
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/help/archiveSupport",
    component: ArchiveSupport,
    exact: true,
    index: 22,
    pkgAccess: "ARCHIVE_SUPPORT",
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/account/settings/appointmentPageSetting",
    component: AppointmentPageSetting,
    exact: true,
    index: 22,
    pkgAccess: "APPOINTMENT_SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/notificationPageSetting",
    component: NotificationPageSetting,
    exact: true,
    index: 22,
    pkgAccess: "NOTIFICATION_SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/alertsPageSetting",
    component: AlertsPageSetting,
    exact: true,
    index: 22,
    pkgAccess: "NOTIFICATION_SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/reportsPageSetting",
    component: ReportsPageSetting,
    exact: true,
    index: 22,
    pkgAccess: "SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/chatPageSetting",
    component: ChatPageSetting,
    exact: true,
    index: 22,
    pkgAccess: "SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/suppliePageSetting",
    component: SuppliePageSetting,
    exact: true,
    index: 22,
    pkgAccess: "SUPPLY_SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/issuePageSetting",
    component: IssuePageSetting,
    exact: true,
    index: 22,
    pkgAccess: "ISSUES_SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/proofOfServiceSetting",
    component: ProofOfServiceSetting,
    exact: true,
    index: 22,
    pkgAccess: "SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/privacy-data-settings",
    component: PrivacyDataSettings,
    exact: true,
    index: 22,
    pkgAccess: "SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/account/settings/payments",
    component: PaymentsRedirectToInvoiceSettings,
    exact: true,
    index: 22,
    pkgAccess: "BILLING",
    secure: true,
    roles: [ROLES.ADMIN, ROLES.COMPANYUSER],
  },
  {
    path: "/account/settings/companyPageSetting",
    component: CompanyPageSetting,
    exact: true,
    index: 22,
    pkgAccess: "CLIENT_SETTINGS",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  // {
  //   path: "/account/settings/invoiceSetting",
  //   component: InvoiceSetting,
  //   exact: true,
  //   index: 22,
  //   pkgAccess: "CREATE_INVOICE",
  //   secure: true,
  //   roles: ACCOUNT_SETTINGS_ROLES,
  // },
  {
    path: "/account/settings/serviceTypesSetting",
    component: ServiceTypesSetting,
    exact: true,
    index: 22,
    pkgAccess: "CREATE_INVOICE",
    secure: true,
    roles: ACCOUNT_SETTINGS_ROLES,
  },
  {
    path: "/createSuperUser",
    component: CreateSuperUser,
    exact: true,
    index: 23,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/createSuperUser/:superUserId",
    component: CreateSuperUser,
    exact: true,
    index: 23,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/superUser",
    component: ManageSuperUser,
    exact: true,
    index: 23,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/createCompany",
    component: CreateCompany,
    exact: true,
    index: 24,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/createCompany/:companyId",
    component: CreateCompany,
    exact: true,
    index: 24,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/company",
    component: ManageCompany,
    exact: true,
    index: 24,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/companyDetails/:companyId",
    component: ManageCompanyDetails,
    exact: true,
    index: 24,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/account/companySubscription",
    component: CompanySubscription,
    exact: true,
    index: 24,
    pkgAccess: "COMPANY_SUBSCRIPTION",
    secure: true,
    roles: [ROLES.SUPERUSER, ROLES.COMPANYUSER, ROLES.ADMIN],
  },
  {
    path: "/account/createUserAdmin",
    component: CreateAdmin,
    exact: true,
    index: 25,
    pkgAccess: "CREATE_ADMIN",
    secure: true,
    roles: [ROLES.SUPERUSER, ROLES.COMPANYUSER, ROLES.ADMIN, EMP_ROLES.ADMIN],
  },
  {
    path: "/account/createUserAdmin/:adminId",
    component: CreateAdmin,
    exact: true,
    index: 25,
    pkgAccess: "CREATE_ADMIN",
    secure: true,
    roles: [ROLES.SUPERUSER, ROLES.COMPANYUSER, ROLES.ADMIN, EMP_ROLES.ADMIN],
  },
  {
    path: "/account/userAdmin",
    component: ManageAdmin,
    exact: true,
    pkgAccess: "MANAGE_ADMIN",
    index: 25,
    secure: true,
    roles: [ROLES.SUPERUSER, ROLES.COMPANYUSER, ROLES.ADMIN, EMP_ROLES.ADMIN],
  },
  {
    path: "/accounts",
    component: Accounts,
    exact: true,
    pkgAccess: "ACCOUNTS",
    index: 26,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/accountsDetails/:accountId",
    component: AccountDetails,
    exact: true,
    pkgAccess: "ACCOUNTS_DETAILS",
    index: 26,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/manageUsers",
    component: ManageUsers,
    exact: true,
    pkgAccess: "MANAGE_USERS",
    index: 27,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/manageUsersDetails/:userId",
    component: UserDetails,
    exact: true,
    pkgAccess: "USERS_DETAILS",
    index: 27,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  // Testing: reviews + review settings routes disabled
  // {
  //   path: "/reviews",
  //   component: ReviewsRedirect,
  //   exact: true,
  //   index: 27.1,
  //   secure: true,
  //   roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  // },
  // {
  //   path: "/reviews/overview",
  //   component: ReviewsOverview,
  //   exact: true,
  //   index: 27.2,
  //   secure: true,
  //   roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  // },
  // {
  //   path: "/reviews/internal",
  //   component: ReviewsInternal,
  //   exact: true,
  //   index: 27.3,
  //   secure: true,
  //   roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  // },
  // {
  //   path: "/reviews/internal/:clientId",
  //   component: ReviewsInternalDetail,
  //   exact: true,
  //   index: 27.31,
  //   secure: true,
  //   roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  // },
  // {
  //   path: "/reviews/google",
  //   component: ReviewsGoogle,
  //   exact: true,
  //   index: 27.4,
  //   secure: true,
  //   roles: [ROLES.ADMIN, ROLES.COMPANYUSER, EMP_ROLES.ADMIN],
  // },
  // {
  //   path: "/account/settings/reviewsPageSetting",
  //   component: ReviewsPageSetting,
  //   exact: true,
  //   index: 27.5,
  //   secure: true,
  //   roles: ACCOUNT_SETTINGS_ROLES,
  // },
  {
    path: "/help/helpTour",
    component: HelpTours,
    exact: true,
    pkgAccess: "TECHNICAL_SUPPORT",
    index: 28,
    secure: true,
    roles: [ROLES.ADMIN, EMP_ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.SUPPORT.toLowerCase(), EMP_ROLES.SUPPORT, EMP_ROLES.CLIENT_TEAM.toLowerCase(), EMP_ROLES.GUEST.toLowerCase(), ROLES.COMPANYUSER, ROLES.CLIENT],
  },
    {
    path: "/superUserDashboard",
    component: SuperUserDashboard,
    exact: true,
    pkgAccess: "SUPERUSER_DASHBOARD",
    index: 29,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/superUser/stripe-connect",
    component: SuperUserStripeConnect,
    exact: true,
    pkgAccess: "SUPERUSER_DASHBOARD",
    index: 29,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/analytics",
    component: Analytics,
    exact: true,
    pkgAccess: "Analytics",
    index: 29,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
  {
    path: "/superUserSettings",
    component: SuperUserSettings,
    exact: true,
    pkgAccess: "SuperUserSettings",
    index: 29,
    secure: true,
    roles: [ROLES.SUPERUSER],
  },
];
const routes = [...authRoute, ...secureRoutes];

export { sidebarMenuList, routes, secureRoutes };
